import { OverviewDTO } from '@Models/Overview.types';
import { APIError, ListResponse, useFetchPaginatedState, useListSearchParam, useSearchParam } from '@tlx/astro-shared';
import useSWRInfinite from 'swr/infinite';
import { FilterState } from '@Component/Filter/utils';
import { OrderByTypeSalaryOverview, TableSortState } from '@Hooks/useOrdering.ts';
import { useDebouncedQuery } from '@Hooks/useDebouncedQuery.ts';
import { APIUrls } from '@Util/urls';

interface Props {
    filters: FilterState;
    sorting: TableSortState<OrderByTypeSalaryOverview>;
}

const count = 25;
const queryDebounceDelay = 250;

export function useOverviewVouchers(props: Props) {
    const content = useSearchParam('content', 'transactions');
    const period = useListSearchParam('period', props.filters.periodFilter.defaultValue);

    const initialQuery = useSearchParam('query', '');
    const query = useDebouncedQuery(initialQuery, queryDebounceDelay);

    const response = useSWRInfinite<ListResponse<OverviewDTO>, APIError>(
        (pageIndex: number, previousPageData: ListResponse<OverviewDTO>) => {
            if (previousPageData && previousPageData.fullResultSize === 0) {
                return null; // no more data left: this stops the swrinfinite-process
            } else {
                const apiParams = new URLSearchParams();

                apiParams.append('from', (pageIndex * count).toString());
                apiParams.append('count', count.toString());
                apiParams.append('startDate', period[0]);
                apiParams.append('endDate', period[1]);

                if (initialQuery.length > 2) {
                    apiParams.append('query', query);
                }

                if (props.sorting.sortColumn) {
                    apiParams.append('sortField', props.sorting.sortColumn);
                }
                if (props.sorting.sortDirection) {
                    apiParams.append('sortOrder', props.sorting.sortDirection === 'ascending' ? 'ASC' : 'DESC');
                }

                if (props.filters.excludeReversedFilter.query) {
                    apiParams.append('excludeReversed', props.filters.excludeReversedFilter.query);
                }
                apiParams.append('fields', '*, approvedBy(id,displayName,pictureId)');
                return `${APIUrls.SALARY_OVERVIEW_BASE}/${content}?${apiParams.toString()}`;
            }
        },
    );

    return { ...useFetchPaginatedState(response), mutate: response.mutate };
}
